import { CaretLeftIcon, CaretRightIcon, XIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { ModalPrimaryButton } from "./Button";

/** Anything with a URL can be expanded. `HydratedImage` satisfies this, and so
 *  does a saved image asset being previewed from the Replace modal. */
export interface ExpandableImage {
  url?: string | null;
  alt?: string;
}

interface ExpandImagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: ExpandableImage[];
  initialIndex?: number;
  /** Name of the expanded thing, shown over the image. */
  title?: string;
  /** Optional primary action, e.g. choosing the previewed asset. */
  action?: { label: string; onClick: () => void };
  /** Raise the overlay above a host modal that also sits at z-50. */
  zClassName?: string;
}

function NavButton({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick: () => void;
}) {
  const Icon = direction === "prev" ? CaretLeftIcon : CaretRightIcon;
  const positionClass = direction === "prev" ? "left-4" : "right-4";
  const label = direction === "prev" ? "Previous image" : "Next image";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`absolute ${positionClass} top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-lg bg-[var(--bg-modal)] hover:bg-[var(--bg-field)] text-[var(--text-primary)] transition`}
      aria-label={label}
    >
      <Icon size={24} weight="bold" />
    </button>
  );
}

function ImageCounter({ current, total }: { current: number; total: number }) {
  return (
    <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-lg bg-[var(--bg-modal)] text-[var(--text-primary)] text-sm font-medium">
      {current + 1}/{total}
    </div>
  );
}

export function ExpandImagesModal({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
  title,
  action,
  zClassName = "z-50",
}: ExpandImagesModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((i) => (i < images.length - 1 ? i + 1 : 0));
  }, [images.length]);

  useHotkeys("escape", onClose, { enabled: isOpen });
  useHotkeys("left", goToPrevious, { enabled: isOpen });
  useHotkeys("right", goToNext, { enabled: isOpen });

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(Math.min(initialIndex, Math.max(0, images.length - 1)));
    }
  }, [isOpen, initialIndex, images.length]);

  if (!isOpen) return null;

  const currentImage = images[currentIndex];
  const imageUrl = currentImage?.url;
  const hasMultiple = images.length > 1;

  return (
    <div
      className={`fixed inset-0 ${zClassName} flex items-center justify-center`}
    >
      <button
        type="button"
        aria-label="Close modal"
        className="absolute inset-0 bg-[var(--bg-default-overlay)] cursor-default"
        onClick={onClose}
      />
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stop-propagation guard, not an interactive element */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stop-propagation guard, not an interactive element */}
      <div
        className="relative bg-white rounded-xl overflow-hidden flex items-center justify-center"
        style={{ width: "calc(100vw - 3rem)", height: "calc(100vh - 3rem)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-lg bg-[var(--bg-modal)] hover:bg-[var(--bg-field)] text-[var(--text-primary)] transition"
          aria-label="Close"
        >
          <XIcon size={22} />
        </button>
        {title && (
          <div className="absolute top-4 left-4 z-10 max-w-[60%] truncate rounded-lg bg-[var(--bg-modal)] px-3 py-1.5 text-sm font-medium text-[var(--text-primary)]">
            {title}
          </div>
        )}
        {hasMultiple && <NavButton direction="prev" onClick={goToPrevious} />}
        <div className="w-full h-full flex items-center justify-center p-12">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={currentImage?.alt ?? `Design option ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <div className="text-gray-500">No image available</div>
          )}
        </div>
        {hasMultiple && <NavButton direction="next" onClick={goToNext} />}
        {hasMultiple && (
          <ImageCounter current={currentIndex} total={images.length} />
        )}
        {action && (
          <div className="absolute bottom-4 right-4 z-10">
            <ModalPrimaryButton onClick={action.onClick}>
              {action.label}
            </ModalPrimaryButton>
          </div>
        )}
      </div>
    </div>
  );
}
